import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import PageHeader from '../components/PageHeader';
import { clientAPI, templateAPI, documentAPI, providerAPI, clientDocumentAPI } from '../services/api';
import { formatDisplayDate } from '../utils/dateFormat';
import { formatProviderDisplayLabel, formatProviderSiteOptionLabel } from '../utils/providerDisplayLabel';
import './DocGenLanding.css';

const DEBOUNCE_MS = 300;

/** Build download filename: "LastName, FirstName - TemplateName.ext" (safe for filesystem). */
function buildDownloadFilename(client, templateDisplayName, ext) {
  const safe = (s) => (s ?? '').toString().replace(/[^\w\s,'-]/g, '').trim() || 'Unknown';
  const name = templateDisplayName ? safe(templateDisplayName).replace(/[/\\]/g, '-') : 'Document';
  return `${safe(client?.last_name)}, ${safe(client?.first_name)} - ${name}.${ext}`;
}

/** Trigger a download with the given blob URL and filename (so the saved file has a proper name, not a UUID). */
function triggerDownload(blobUrl, filename) {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Flatten folder tree to { key, label } list; nested folders get indented label. */
function flattenFolderOptions(folders, prefix = '') {
  if (!folders || !folders.length) return [];
  const out = [];
  for (const f of folders) {
    const label = prefix ? `${prefix} → ${f.name}` : f.name;
    out.push({ key: f.key, label });
    if (f.children?.length) out.push(...flattenFolderOptions(f.children, f.name));
  }
  return out;
}

/** Find folder by key in tree (for fileTemplates / suggested names). */
function findFolderByKey(folders, key) {
  if (!folders || !key) return null;
  for (const folder of folders) {
    if (folder.key === key) return folder;
    if (folder.children?.length) {
      const found = findFolderByKey(folder.children, key);
      if (found) return found;
    }
  }
  return null;
}

/** Clean a template suggestion for use as filename base (strip ~ and extension). */
function cleanSuggestionForName(suggestion, fileExt) {
  if (typeof suggestion !== 'string') return '';
  let clean = suggestion.replace(/^~+|~+$/g, '').trim();
  const ext = clean.substring(clean.lastIndexOf('.')).toLowerCase();
  if (ext === '.pdf' || ext === '.docx' || ext === '.doc') clean = clean.substring(0, clean.lastIndexOf('.'));
  return (clean.trim() || '') + (fileExt || '');
}

/** Categories shown in the unified template list (single-doc generate). MonthlyContact and ATPacket are separate flows. */
const UNIFIED_LIST_CATEGORIES = ['Client', 'Document', 'Transfer', 'ASQ', 'FaxScript', 'TPC'];

function DocGenLanding({ embeddedInClient = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantPath } = useTenantFromUrl();
  const preselectedClientId = searchParams.get('clientId');

  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientSearchRef = useRef(null);

  const [allTemplates, setAllTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateLanguageFilter, setTemplateLanguageFilter] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('');
  const [generatingTemplateId, setGeneratingTemplateId] = useState(null);
  const [generateSuccess, setGenerateSuccess] = useState(null);
  const [generateError, setGenerateError] = useState(null);

  const [promptModalTemplate, setPromptModalTemplate] = useState(null);
  const [promptValues, setPromptValues] = useState({});
  /** DR provider+site rows for doc-gen doctor picker (same source as pediatrician site dropdown). */
  const [doctorSiteOptions, setDoctorSiteOptions] = useState([]);
  /** Fallback when no sites exist: DR providers only (value prefix provider:). */
  const [doctorProvidersFallback, setDoctorProvidersFallback] = useState([]);
  const [doctorOptionsLoading, setDoctorOptionsLoading] = useState(false);
  const [promptModalGenerating, setPromptModalGenerating] = useState(false);
  const [promptModalError, setPromptModalError] = useState(null);

  const [infoOpen, setInfoOpen] = useState({ client: false, templates: false });
  const toggleInfo = (key) => setInfoOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  const [asqRecommendation, setAsqRecommendation] = useState(null);
  const [addToFolderLoading, setAddToFolderLoading] = useState(false);
  const [addToFolderError, setAddToFolderError] = useState(null);
  const [addToFolderSuccess, setAddToFolderSuccess] = useState(null);
  const [addToFolderModalOpen, setAddToFolderModalOpen] = useState(false);
  const [folderOptions, setFolderOptions] = useState([]);
  const [folderTemplate, setFolderTemplate] = useState(null);
  const [selectedFolderKeyForUpload, setSelectedFolderKeyForUpload] = useState('');
  const [saveAsNameForUpload, setSaveAsNameForUpload] = useState('');
  const [folderTemplateLoading, setFolderTemplateLoading] = useState(false);
  const [addedToFolderName, setAddedToFolderName] = useState(null);
  const [lastGeneratedTemplate, setLastGeneratedTemplate] = useState(null);

  // Revoke blob URL on unmount to avoid leaking object URLs
  useEffect(() => {
    return () => {
      if (generateSuccess?.blobUrl) window.URL.revokeObjectURL(generateSuccess.blobUrl);
    };
  }, [generateSuccess?.blobUrl]);

  // Pre-select client from query (e.g. from client record "Generate document")
  useEffect(() => {
    if (preselectedClientId && !selectedClient) {
      clientAPI.getById(preselectedClientId).then((res) => {
        const c = res.data?.data;
        if (c) setSelectedClient(c);
      }).catch(() => {});
    }
  }, [preselectedClientId, selectedClient]);

  // Client search typeahead
  const runClientSearch = useCallback((q) => {
    if (!q || q.trim().length < 2) {
      setClientResults([]);
      return;
    }
    setClientLoading(true);
    clientAPI.getAll({ q: q.trim(), page: 1, pageSize: 500 })
      .then((res) => {
        setClientResults(res.data?.data ?? []);
      })
      .catch(() => setClientResults([]))
      .finally(() => setClientLoading(false));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      runClientSearch(clientSearch);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [clientSearch, runClientSearch]);

  const openDropdown = () => setClientDropdownOpen(true);
  const closeDropdown = () => setTimeout(() => setClientDropdownOpen(false), 200);

  const selectClient = (client) => {
    // Refetch full client so we have latest saved data (e.g. gestation_week) for doc gen and ASQ
    clientAPI.getById(client.id).then((res) => {
      const full = res.data?.data;
      setSelectedClient(full || client);
    }).catch(() => setSelectedClient(client));
    setClientSearch('');
    setClientResults([]);
    setClientDropdownOpen(false);
  };

  const clearClient = () => {
    setSelectedClient(null);
    setClientSearch('');
  };

  // Load all single-doc templates when we have a client (one list, age-filtered by backend)
  useEffect(() => {
    if (!selectedClient?.id) {
      setAllTemplates([]);
      setAsqRecommendation(null);
      return;
    }
    setTemplatesLoading(true);
    templateAPI.getAll({ clientId: selectedClient.id })
      .then((res) => {
        const list = res.data?.templates ?? res.data?.data ?? [];
        const filtered = list.filter((t) => UNIFIED_LIST_CATEGORIES.includes(t.category));
        setAllTemplates(filtered);
        setAsqRecommendation(res.data?.asqRecommendation ?? null);
      })
      .catch(() => {
        setAllTemplates([]);
        setAsqRecommendation(null);
      })
      .finally(() => setTemplatesLoading(false));
  }, [selectedClient?.id]);

  // When prompt modal opens and template needs providerId, load DR sites (not paginated all-types list).
  useEffect(() => {
    if (!promptModalTemplate?.promptTags?.includes('providerId')) return;
    setDoctorOptionsLoading(true);
    Promise.all([
      providerAPI.getByTypeSites('DR'),
      providerAPI.getByType('DR'),
    ])
      .then(([sitesRes, typeRes]) => {
        const sites = sitesRes.data?.data ?? [];
        const dr = typeRes.data?.data ?? [];
        setDoctorSiteOptions(Array.isArray(sites) ? sites : []);
        setDoctorProvidersFallback(Array.isArray(dr) ? dr : []);
      })
      .catch(() => {
        setDoctorSiteOptions([]);
        setDoctorProvidersFallback([]);
      })
      .finally(() => setDoctorOptionsLoading(false));
  }, [promptModalTemplate?.id]);

  const doGenerate = async (template, prompts, providerId, providerSiteId) => {
    if (!selectedClient?.id || !template?.id) return;
    setGenerateError(null);
    setGenerateSuccess((prev) => {
      if (prev?.blobUrl) window.URL.revokeObjectURL(prev.blobUrl);
      return null;
    });
    setGeneratingTemplateId(template.id);
    try {
      const response = await documentAPI.generateForClient(selectedClient.id, template.id, prompts || {}, providerId, providerSiteId);
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
      if (contentType.includes('application/json')) {
        const errorData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        throw new Error(errorData.error || errorData.message || 'Failed to generate document');
      }
      const ext = (template?.fileName || '').toLowerCase().endsWith('.pdf') ? 'pdf' : 'docx';
      const filename = buildDownloadFilename(selectedClient, template?.displayName, ext);
      const blobType = contentType.includes('pdf') ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isPdf = contentType.includes('pdf');
      const blob = new Blob([response.data], { type: blobType });
      const url = window.URL.createObjectURL(blob);
      setGenerateSuccess((prev) => {
        if (prev?.blobUrl) window.URL.revokeObjectURL(prev.blobUrl);
        return { displayName: template?.displayName || 'Document', filename, blobUrl: url, isPdf };
      });
      setLastGeneratedTemplate(template || null);
      setPromptModalTemplate(null);
      setPromptValues({});
    } catch (err) {
      setGenerateError(err.message || 'Failed to generate document');
      if (promptModalTemplate) setPromptModalError(err.message || 'Failed to generate document');
    } finally {
      setGeneratingTemplateId(null);
      setPromptModalGenerating(false);
    }
  };

  const handleGenerateClick = (template) => {
    const tags = template.promptTags || [];
    if (tags.length === 0) {
      doGenerate(template, {}, null);
      return;
    }
    setPromptModalError(null);
    setPromptValues({});
    setPromptModalTemplate(template);
  };

  const handlePromptModalGenerate = () => {
    if (!promptModalTemplate || !selectedClient?.id) return;
    const prompts = {};
    (promptModalTemplate.promptTags || []).forEach((tag) => {
      if (tag === 'providerId') return;
      const v = promptValues[tag];
      if (v != null && String(v).trim() !== '') prompts[tag] = String(v).trim();
    });
    if (promptModalTemplate.promptTags?.includes('INTAKE_DATE') || promptModalTemplate.category === 'ASQ') {
      const gw = selectedClient.gestation_week != null && selectedClient.gestation_week !== '' ? Number(selectedClient.gestation_week) : null;
      const premWeeks = (gw != null && gw <= 37) ? String(40 - gw) : '';
      prompts.CL_PREMATURE_WEEK = premWeeks;
      prompts.CL_PreMatureWeek = premWeeks;
      if (gw != null && gw !== '') prompts.CL_GESTATION_WEEK = String(gw);
    }
    let providerId = null;
    let providerSiteId = null;
    if (promptModalTemplate.promptTags?.includes('providerId')) {
      const raw = promptValues.providerId;
      if (raw == null || String(raw).trim() === '') {
        setPromptModalError('Please select a doctor.');
        return;
      }
      const rawStr = String(raw).trim();
      if (rawStr.startsWith('provider:')) {
        providerId = rawStr.replace(/^provider:/, '');
      } else {
        const opt = doctorSiteOptions.find((o) => String(o.payee_site_id ?? o.provider_site_id) === rawStr);
        if (!opt) {
          setPromptModalError('Please select a doctor.');
          return;
        }
        providerId = String(opt.provider_id);
        providerSiteId = String(opt.payee_site_id ?? opt.provider_site_id);
      }
    }
    setPromptModalGenerating(true);
    setPromptModalError(null);
    doGenerate(promptModalTemplate, prompts, providerId, providerSiteId);
  };

  const filteredTemplates = allTemplates.filter((t) => {
    const matchSearch = !templateSearch.trim() || (t.displayName || '').toLowerCase().includes(templateSearch.toLowerCase());
    const matchLang = !templateLanguageFilter || (t.language || '') === templateLanguageFilter;
    const matchCat = !templateCategoryFilter || (t.category || '') === templateCategoryFilter;
    return matchSearch && matchLang && matchCat;
  });

  const templateCategories = [...new Set(allTemplates.map((t) => t.category).filter(Boolean))].sort();

  const hasClient = !!selectedClient?.id;

  const handlePreviewPrint = () => {
    if (!generateSuccess?.blobUrl) return;
    const w = window.open(generateSuccess.blobUrl, '_blank', 'noopener');
    if (w) w.onload = () => { w.print(); w.close(); };
  };

  const handlePreviewDownload = () => {
    if (!generateSuccess?.blobUrl || !generateSuccess?.filename) return;
    triggerDownload(generateSuccess.blobUrl, generateSuccess.filename);
  };

  const openAddToFolderModal = async () => {
    if (!selectedClient?.id || !generateSuccess?.filename) return;
    setAddToFolderModalOpen(true);
    setAddToFolderError(null);
    setAddToFolderSuccess(null);
    setAddedToFolderName(null);
    setFolderOptions([]);
    setFolderTemplate(null);
    setSelectedFolderKeyForUpload('');
    setSaveAsNameForUpload(generateSuccess.filename);
    setFolderTemplateLoading(true);
    try {
      const templateRes = await clientDocumentAPI.getTemplate();
      const template = templateRes?.data?.data;
      setFolderTemplate(template);
      const folders = template?.template_structure?.folders;
      const options = flattenFolderOptions(folders || []);
      setFolderOptions(options);
      const defaultKey = lastGeneratedTemplate?.defaultFolderKey;
      const matchDefault = defaultKey && options.some((o) => o.key === defaultKey);
      if (matchDefault) {
        setSelectedFolderKeyForUpload(defaultKey);
      } else if (options.length) {
        setSelectedFolderKeyForUpload(options[0].key);
      }
    } catch (err) {
      setAddToFolderError(err.response?.data?.error || err.message || 'Could not load folder list.');
    } finally {
      setFolderTemplateLoading(false);
    }
  };

  const selectedFolderForUpload = folderTemplate?.template_structure?.folders
    ? findFolderByKey(folderTemplate.template_structure.folders, selectedFolderKeyForUpload)
    : null;
  const addToFolderSuggestions = (selectedFolderForUpload?.fileTemplates || []).filter(
    (s) => s != null && String(s).trim() !== ''
  );
  const generatedFileExt = generateSuccess?.filename ? generateSuccess.filename.substring(generateSuccess.filename.lastIndexOf('.')).toLowerCase() : '.pdf';

  const handleAddToClientFolderConfirm = async () => {
    if (!generateSuccess?.blobUrl || !generateSuccess?.filename || !selectedClient?.id || !selectedFolderKeyForUpload) return;
    let finalName = (saveAsNameForUpload || '').trim() || generateSuccess.filename;
    if (!finalName.toLowerCase().includes('.')) finalName = finalName + generatedFileExt;
    setAddToFolderError(null);
    setAddToFolderSuccess(null);
    setAddToFolderLoading(true);
    const folderLabel = folderOptions.find((o) => o.key === selectedFolderKeyForUpload)?.label || selectedFolderKeyForUpload;
    try {
      const res = await fetch(generateSuccess.blobUrl);
      const blob = await res.blob();
      const file = new File([blob], finalName, { type: blob.type || 'application/octet-stream' });
      await clientDocumentAPI.uploadFile(selectedClient.id, selectedFolderKeyForUpload, file, finalName);
      setAddedToFolderName(folderLabel);
      setAddToFolderSuccess(true);
      setAddToFolderModalOpen(false);
    } catch (err) {
      setAddToFolderError(err.response?.data?.error || err.message || 'Failed to add to client folder.');
    } finally {
      setAddToFolderLoading(false);
    }
  };

  const dismissPreview = () => {
    if (generateSuccess?.blobUrl) window.URL.revokeObjectURL(generateSuccess.blobUrl);
    setGenerateSuccess(null);
    setAddToFolderError(null);
    setAddToFolderSuccess(null);
    setAddedToFolderName(null);
  };

  return (
    <div className={`docgen-landing ${generateSuccess ? 'docgen-landing-with-preview' : ''}`}>
      <PageHeader title="Document Generation (Doc Gen)" />

      <div className="docgen-landing-main">
      {!embeddedInClient && (
        <section className="docgen-section docgen-panel docgen-client-selector" aria-label="Client selection">
          <h2 className="docgen-panel-title">
            Client
            <button type="button" className="docgen-info-icon" onClick={() => toggleInfo('client')} aria-expanded={infoOpen.client} aria-controls="docgen-info-client" aria-label="Toggle help">ⓘ</button>
          </h2>
          {infoOpen.client && <p id="docgen-info-client" className="docgen-info-text">Select a client to see document options for that client.</p>}
          {selectedClient ? (
            <div className="docgen-client-selected">
              <span className="docgen-client-label">Selected:</span>
              <strong>
                {selectedClient.last_name}, {selectedClient.first_name}
                {selectedClient.ei_number && ` (${selectedClient.ei_number})`}
              </strong>
              <button type="button" className="docgen-btn docgen-btn-primary" onClick={clearClient}>
                Change client
              </button>
            </div>
          ) : (
            <div className="docgen-client-search-wrap" ref={clientSearchRef}>
              <label htmlFor="docgen-client-search" className="docgen-sr-only">Search by client name or EI number</label>
              <input
                id="docgen-client-search"
                type="text"
                className="docgen-input docgen-client-input"
                placeholder="Search by client name or EI number..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                onFocus={openDropdown}
                onBlur={closeDropdown}
                autoComplete="off"
                aria-expanded={clientDropdownOpen}
                aria-controls="docgen-client-results"
                aria-autocomplete="list"
              />
              {clientLoading && <span className="docgen-input-loading" aria-live="polite">Searching…</span>}
              {clientDropdownOpen && (clientSearch.length >= 2 || clientResults.length > 0) && (
                <ul id="docgen-client-results" className="docgen-client-results" role="listbox">
                  {clientResults.length === 0 && !clientLoading && (
                    <li className="docgen-client-result-item docgen-no-results">Type at least 2 characters to search.</li>
                  )}
                  {clientResults.map((c) => (
                    <li
                      key={c.id}
                      role="option"
                      className="docgen-client-result-item"
                      onPointerDown={(e) => { e.preventDefault(); selectClient(c); }}
                    >
                      {c.last_name}, {c.first_name}
                      {c.ei_number && ` · ${c.ei_number}`}
                      {c.date_of_birth && ` · DOB ${formatDisplayDate(c.date_of_birth)}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {generateError && (
        <div className="docgen-alert docgen-alert-error" role="alert">
          {generateError}
        </div>
      )}

      <section className="docgen-section docgen-panel docgen-templates-unified">
        <h2 className="docgen-panel-title">
          Templates for this client
          <button type="button" className="docgen-info-icon" onClick={() => toggleInfo('templates')} aria-expanded={infoOpen.templates} aria-controls="docgen-info-templates" aria-label="Toggle help">ⓘ</button>
        </h2>
        {infoOpen.templates && <p id="docgen-info-templates" className="docgen-info-text">Templates are filtered by client age when applicable. Choose a template and click Generate; if the template needs a date or doctor, you'll be asked before generating.</p>}
        {!hasClient && (
          <p className="docgen-card-hint">Select a client above to see templates.</p>
        )}
        {hasClient && (
          <>
            {asqRecommendation && filteredTemplates.some((t) => t.category === 'ASQ') && (
              <div className="docgen-asq-recommendation" role="status">
                <span className="docgen-asq-recommendation-label">Client adjusted age:</span>
                {' '}
                {asqRecommendation.adjustedAgeMonths != null && asqRecommendation.adjustedAgeDays != null
                  ? `${asqRecommendation.adjustedAgeMonths} months ${asqRecommendation.adjustedAgeDays} days`
                  : asqRecommendation.adjustedAgeMonths != null
                    ? `${asqRecommendation.adjustedAgeMonths} months`
                    : '—'}
                {asqRecommendation.asqFormLabel && (
                  <>
                    {' '}
                    <span className="docgen-asq-recommendation-therefore">Therefore recommended ASQ:</span>
                    {' '}
                    <strong>{asqRecommendation.asqFormLabel}</strong>
                  </>
                )}
                {asqRecommendation.message && (
                  <span className="docgen-asq-recommendation-message"> {asqRecommendation.message}</span>
                )}
              </div>
            )}
            {templatesLoading ? (
              <p className="docgen-loading">Loading templates…</p>
            ) : (
              <div className="docgen-templates-table-wrap">
                <table className="docgen-templates-table">
                  <thead>
                    <tr>
                      <th>Template</th>
                      <th className="docgen-th-category">Category</th>
                      <th className="docgen-th-lang" title="Language">Lang</th>
                      <th className="docgen-th-action">Action</th>
                    </tr>
                    <tr className="docgen-templates-filter-row">
                      <th>
                        <input
                          type="text"
                          className="docgen-input docgen-table-filter-input"
                          placeholder="Search templates..."
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          aria-label="Filter by template name"
                        />
                      </th>
                      <th>
                        <select
                          className="docgen-select docgen-table-filter-select"
                          value={templateCategoryFilter}
                          onChange={(e) => setTemplateCategoryFilter(e.target.value)}
                          aria-label="Filter by category"
                        >
                          <option value="">All</option>
                          {templateCategories.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </th>
                      <th>
                        <select
                          className="docgen-select docgen-table-filter-select"
                          value={templateLanguageFilter}
                          onChange={(e) => setTemplateLanguageFilter(e.target.value)}
                          aria-label="Filter by language"
                        >
                          <option value="">All</option>
                          <option value="EN">E</option>
                          <option value="ES">S</option>
                        </select>
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTemplates.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="docgen-templates-empty">
                          {allTemplates.length === 0 ? 'No templates for this client.' : 'No templates match the filters.'}
                        </td>
                      </tr>
                    ) : (
                      filteredTemplates.map((t) => (
                        <tr key={t.id} className="docgen-template-tr">
                          <td className="docgen-td-name">{t.displayName || t.templateCode || '—'}</td>
                          <td className="docgen-td-category">{t.category === 'Document' || t.category === 'Transfer' ? 'Client' : (t.category || '—')}</td>
                          <td className="docgen-td-lang" title={t.language === 'EN' ? 'English' : t.language === 'ES' ? 'Spanish' : ''}>
                            {t.language === 'EN' ? 'E' : t.language === 'ES' ? 'S' : (t.language || '—')}
                          </td>
                          <td className="docgen-td-action">
                            <button
                              type="button"
                              className="docgen-btn docgen-btn-generate-icon"
                              onClick={() => handleGenerateClick(t)}
                              disabled={generatingTemplateId === t.id}
                              aria-busy={generatingTemplateId === t.id}
                              title={generatingTemplateId === t.id ? 'Generating…' : 'Generate'}
                              aria-label={generatingTemplateId === t.id ? 'Generating…' : `Generate ${t.displayName || t.templateCode || 'document'}`}
                            >
                              {generatingTemplateId === t.id ? (
                                <span className="docgen-generate-spinner" aria-hidden="true" />
                              ) : (
                                <svg className="docgen-icon-generate" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <polygon points="5 3 19 12 5 21 5 3" />
                                </svg>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      </div>

      {generateSuccess && (
        <aside className="docgen-preview-panel" aria-label="Document preview">
          <div className="docgen-preview-toolbar">
            <span className="docgen-preview-toolbar-title">{generateSuccess.displayName}</span>
            <div className="docgen-preview-toolbar-actions">
              <button type="button" className="docgen-btn docgen-btn-secondary" onClick={dismissPreview}>
                Close
              </button>
              <button type="button" className="docgen-btn docgen-btn-primary" onClick={handlePreviewPrint}>
                Print
              </button>
              <button type="button" className="docgen-btn docgen-btn-primary" onClick={handlePreviewDownload}>
                Download
              </button>
              <button
                type="button"
                className="docgen-btn docgen-btn-primary"
                onClick={openAddToFolderModal}
                disabled={addToFolderLoading || !selectedClient?.id}
              >
                {addToFolderLoading ? 'Adding…' : 'Add to client folder'}
              </button>
            </div>
          </div>
          {addToFolderError && <div className="docgen-alert docgen-alert-error" role="alert">{addToFolderError}</div>}
          {addToFolderSuccess && addedToFolderName && (
            <div className="docgen-alert docgen-alert-success" role="alert">
              Added to <strong>{addedToFolderName}</strong>.{' '}
              <a href={tenantPath(`/client/${selectedClient?.id}/folder`)}>View in Client Folder →</a>
            </div>
          )}
          <div className="docgen-preview-content">
            {generateSuccess.isPdf ? (
              <iframe title="Document preview" src={generateSuccess.blobUrl} className="docgen-preview-iframe" />
            ) : (
              <div className="docgen-preview-placeholder">
                <p>Document ready. Use the buttons above to <strong>Print</strong>, <strong>Download</strong>, or <strong>Add to client folder</strong>.</p>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Add to client folder: pick folder from template then upload */}
      {addToFolderModalOpen && (
        <div
          className="docgen-modal-overlay"
          onClick={() => { if (!addToFolderLoading) { setAddToFolderModalOpen(false); setAddToFolderError(null); } }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-to-folder-modal-title"
        >
          <div className="docgen-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="add-to-folder-modal-title">Add to client folder</h3>
            <p className="docgen-modal-desc">Choose where to put this document in the client&apos;s folder.</p>
            {addToFolderError && <div className="docgen-modal-error" role="alert">{addToFolderError}</div>}
            {folderTemplateLoading ? (
              <p>Loading folders…</p>
            ) : folderOptions.length === 0 ? (
              <p className="docgen-modal-hint">No folders available. Check your folder template in Settings.</p>
            ) : (
              <>
                <label className="docgen-modal-field">
                  Folder
                  <select
                    className="docgen-select"
                    value={selectedFolderKeyForUpload}
                    onChange={(e) => setSelectedFolderKeyForUpload(e.target.value)}
                    aria-label="Choose folder"
                  >
                    {folderOptions.map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                <div className="docgen-modal-field docgen-save-as-row">
                  <label htmlFor="docgen-add-to-folder-saveas">Save as:</label>
                  <div className="docgen-save-as-input-wrap">
                    <input
                      id="docgen-add-to-folder-saveas"
                      type="text"
                      className="docgen-input"
                      value={saveAsNameForUpload}
                      onChange={(e) => setSaveAsNameForUpload(e.target.value)}
                      aria-label="File name to save as"
                    />
                  </div>
                </div>
                <div className="docgen-modal-suggestions">
                  <label>Suggested names (from template):</label>
                  {addToFolderSuggestions.length > 0 ? (
                    <div className="docgen-suggestion-buttons">
                      {addToFolderSuggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className="docgen-suggestion-btn"
                          onClick={() => setSaveAsNameForUpload(cleanSuggestionForName(s, generatedFileExt))}
                          title={s}
                        >
                          {cleanSuggestionForName(s, '') || s}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="docgen-modal-hint">No suggested names for this folder. Add them in Settings → Client Folder Template.</p>
                  )}
                </div>
                <p className="docgen-modal-hint docgen-open-folder-hint">
                  Same naming options as when uploading from the client&apos;s{' '}
                  <a href={tenantPath(`/client/${selectedClient?.id}/folder`)}>Client Folder</a>.
                </p>
              </>
            )}
            <div className="docgen-modal-actions">
              <button
                type="button"
                className="docgen-btn docgen-btn-secondary"
                onClick={() => { setAddToFolderModalOpen(false); setAddToFolderError(null); }}
                disabled={addToFolderLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="docgen-btn docgen-btn-primary"
                onClick={handleAddToClientFolderConfirm}
                disabled={addToFolderLoading || folderTemplateLoading || !selectedFolderKeyForUpload || !(saveAsNameForUpload || '').trim()}
              >
                {addToFolderLoading ? 'Adding…' : 'Add here'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single prompt modal: shows only the fields this template needs (from promptTags) */}
      {promptModalTemplate && (
        <div className="docgen-modal-overlay" onClick={() => { setPromptModalTemplate(null); setPromptValues({}); setPromptModalError(null); }} role="dialog" aria-modal="true" aria-labelledby="prompt-modal-title">
          <div className="docgen-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="prompt-modal-title">Generate: {promptModalTemplate.displayName}</h3>
            <p className="docgen-modal-desc">Client: {selectedClient?.last_name}, {selectedClient?.first_name}. Fill in any required fields below.</p>
            {promptModalError && <div className="docgen-modal-error" role="alert">{promptModalError}</div>}
            <div className="docgen-modal-fields">
              {(promptModalTemplate.promptTags || []).includes('INTAKE_DATE') && (
                <label>
                  Intake date
                  <input
                    type="date"
                    className="docgen-input"
                    value={promptValues.INTAKE_DATE || ''}
                    onChange={(e) => setPromptValues((p) => ({ ...p, INTAKE_DATE: e.target.value }))}
                    aria-label="Intake date"
                  />
                </label>
              )}
              {(promptModalTemplate.promptTags || []).includes('TPC_DATE') && (
                <label>
                  TPC date
                  <input
                    type="date"
                    className="docgen-input"
                    value={promptValues.TPC_DATE || ''}
                    onChange={(e) => setPromptValues((p) => ({ ...p, TPC_DATE: e.target.value }))}
                    aria-label="TPC date"
                  />
                </label>
              )}
              {(promptModalTemplate.promptTags || []).includes('providerId') && (
                <label>
                  Doctor
                  {doctorSiteOptions.length > 0 ? (
                    <select
                      className="docgen-select"
                      value={promptValues.providerId || ''}
                      onChange={(e) => setPromptValues((p) => ({ ...p, providerId: e.target.value }))}
                      disabled={doctorOptionsLoading}
                      aria-label="Select doctor and practice location"
                    >
                      <option value="">Select doctor (and location)…</option>
                      {doctorSiteOptions.map((opt) => {
                        const oid = opt.payee_site_id ?? opt.provider_site_id;
                        return (
                        <option key={`${opt.provider_id}-${oid}`} value={String(oid)}>
                          {formatProviderSiteOptionLabel(opt)}
                        </option>
                        );
                      })}
                    </select>
                  ) : (
                    <select
                      className="docgen-select"
                      value={promptValues.providerId || ''}
                      onChange={(e) => setPromptValues((p) => ({ ...p, providerId: e.target.value }))}
                      disabled={doctorOptionsLoading}
                      aria-label="Select doctor"
                    >
                      <option value="">Select doctor…</option>
                      {doctorProvidersFallback.map((p) => (
                        <option key={p.id} value={`provider:${p.id}`}>
                          {formatProviderDisplayLabel(p)}
                        </option>
                      ))}
                    </select>
                  )}
                  {!doctorOptionsLoading && doctorSiteOptions.length === 0 && doctorProvidersFallback.length === 0 && (
                    <span className="docgen-modal-hint">No doctors in the system. Add providers (type DR) in Settings.</span>
                  )}
                  {!doctorOptionsLoading && doctorSiteOptions.length === 0 && doctorProvidersFallback.length > 0 && (
                    <span className="docgen-modal-hint">These doctors have no practice sites on file; fax/address may be incomplete until sites are added.</span>
                  )}
                </label>
              )}
            </div>
            <div className="docgen-modal-actions">
              <button type="button" className="docgen-btn docgen-btn-secondary" onClick={() => { setPromptModalTemplate(null); setPromptValues({}); setPromptModalError(null); }}>Cancel</button>
              <button
                type="button"
                className="docgen-btn docgen-btn-primary"
                disabled={promptModalGenerating}
                onClick={handlePromptModalGenerate}
                aria-busy={promptModalGenerating}
              >
                {promptModalGenerating ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocGenLanding;
